import {
  type CellContext,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table';
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type Group,
  useCreateGroupMutation,
  useGetGroups,
  useUpdateGroupMutation,
} from '~entities/groups';
import { useDeleteGroupMutation } from '~entities/groups/api/delete-group';

import { EnrollioTable } from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

const columnHelper = createColumnHelper<Group>();

const columns = [
  columnHelper.accessor('fieldName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Group Name'
        className='pl-4 text-black'
      />
    ),
    cell: (info) => <div className='px-4 capitalize'>{info.getValue()}</div>,
  }),

  columnHelper.display({
    id: 'Action',
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title='Action'
        className='justify-end px-4 text-black'
      />
    ),
    cell: (info) => {
      return <GroupActionCell info={info} />;
    },
  }),
];

export function GroupListPage() {
  const createMutation = useCreateGroupMutation();

  const [query, setQuery] = useState<string>('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetGroups({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    ...(query ? { search: query } : {}),
  });

  const tableData = data?.items ?? [];

  const handleCreateGroup = async () => {
    try {
      await createMutation.mutateAsync({
        fieldName: newGroupName,
      });
      setIsAddDialogOpen(false);
      setNewGroupName('');
      toast.success('Group created successfully');
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  return (
    <div className='w-full min-w-0 md:w-1/2 lg:w-1/3'>
      <EnrollioTable
        ariaLabel='Groups'
        totalLabel='Groups'
        data={tableData}
        columns={columns}
        rowCount={data?.pagination.total}
        isUpdating={isFetching}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        filterHeader={() => {
          return (
            <div className='flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
              <Input
                placeholder='Search Groups'
                value={query}
                className='w-full sm:w-3/12'
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
              />
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant='secondary'
                    size='sm'
                    className='w-full sm:w-auto'
                  >
                    <PlusIcon />
                    Add New
                  </Button>
                </DialogTrigger>
                <DialogContent className='sm:max-w-[425px]'>
                  <DialogHeader>
                    <DialogTitle>Add New Group</DialogTitle>
                    <DialogDescription>
                      Create a new group by entering the name below.
                    </DialogDescription>
                  </DialogHeader>
                  <div className='grid gap-4 py-4'>
                    <div className='grid grid-cols-4 items-center gap-4'>
                      <Label htmlFor='name' className='text-right'>
                        Group Name
                      </Label>
                      <Input
                        id='name'
                        value={newGroupName}
                        onChange={(event) => {
                          setNewGroupName(event.target.value);
                        }}
                        className='col-span-3'
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant={'outline'}
                      onClick={() => {
                        setIsAddDialogOpen(false);
                        setNewGroupName('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      onClick={handleCreateGroup}
                      disabled={
                        createMutation.isPending || !newGroupName.trim()
                      }
                    >
                      {createMutation.isPending ? (
                        <Loader2Icon className='animate-spin' />
                      ) : (
                        'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        }}
      />
    </div>
  );
}

function GroupActionCell({ info }: { info: CellContext<Group, unknown> }) {
  const mutation = useUpdateGroupMutation();
  const deleteMutation = useDeleteGroupMutation();

  const initialGroupName = info.row.original.fieldName;
  const groupId = info.row.original._id;

  const [groupName, setGroupName] = useState(initialGroupName);
  const [isOpen, setIsOpen] = useState(false);

  const handleSave = async () => {
    try {
      await mutation.mutateAsync({
        fieldId: groupId,
        fieldName: groupName,
      });
      setIsOpen(false);
      toast.success('Group updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update group');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({
        fieldId: groupId,
      });
      toast.success('Group deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete group');
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <div className='flex items-center justify-end gap-2 px-4'>
          <TooltipProvider>
            <Tooltip>
              <DialogTrigger asChild>
                <TooltipTrigger asChild>
                  <Button className='size-6 p-0 text-secondary [&_svg]:size-3'>
                    <PencilIcon />
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent>
                <p className='text-secondary'>Edit Group</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={'destructive'}
                  className='size-6 p-0 [&_svg]:size-3'
                  onClick={handleDelete}
                >
                  <Trash2Icon />
                </Button>
              </TooltipTrigger>
              <TooltipContent className='bg-destructive'>
                <p className='text-destructive-foreground'>Delete Group</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
            <DialogDescription>
              Make changes to your Group here. Click save when you&apos;re done.
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='name' className='text-right'>
                Group Name
              </Label>
              <Input
                id='name'
                value={groupName}
                onChange={(event) => {
                  setGroupName(event.target.value);
                }}
                className='col-span-3'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant={'outline'}
              onClick={() => {
                setIsOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <Loader2Icon className='animate-spin' />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
